import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { FiEdit2, FiPlus, FiRefreshCw } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import apiClient from '@/services/letsparkyClient';
import { useAuthStore } from '@/stores/authStore';
import type {
  Device,
  ParkingArea,
  ParkingAreaCreateParams,
  ParkingAreaDeviceAssignment,
  ParkingAreaUpdateParams,
} from '@letsparky/api-v2-client';
import { ParkingAreaType } from '@letsparky/api-v2-client';

function optionalNumberFromInput(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
}

function rateInputValue(value: number | undefined | null): string {
  if (value == null || Number.isNaN(value)) return '';
  return String(value);
}

type ParkingAreaDeviceRow = NonNullable<ParkingArea['devices']>[number];

/** API may return `status` / `is_active` before the client types are updated */
type ParkingAreaExtended = ParkingArea & {
  status?: string;
  is_active?: boolean;
};

function resolveParkingAreaActivation(area: ParkingArea): 'active' | 'inactive' | 'unknown' {
  const a = area as ParkingAreaExtended;
  if (typeof a.is_active === 'boolean') {
    return a.is_active ? 'active' : 'inactive';
  }
  const s = a.status;
  if (typeof s === 'string') {
    const u = s.toUpperCase();
    if (['ACTIVE', 'ENABLED', 'OPEN'].includes(u)) return 'active';
    if (['INACTIVE', 'DISABLED', 'CLOSED', 'ARCHIVED'].includes(u)) return 'inactive';
  }
  return 'unknown';
}

function formatRateAmount(amount: number | undefined, currency: string | undefined): string {
  if (amount == null || Number.isNaN(amount)) return '—';
  const code = currency?.trim();
  if (code && /^[A-Za-z]{3}$/.test(code)) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code.toUpperCase(),
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      // invalid currency code for Intl
    }
  }
  const formatted = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  return code ? `${formatted} ${code}` : formatted;
}

function rentalSummaryInline(area: ParkingArea): string | null {
  if (area.hourly_rate != null && !Number.isNaN(area.hourly_rate)) {
    return `${formatRateAmount(area.hourly_rate, area.currency)}/h`;
  }
  if (area.first_hour_rate != null && !Number.isNaN(area.first_hour_rate)) {
    return formatRateAmount(area.first_hour_rate, area.currency);
  }
  if (area.max_daily_rate != null && !Number.isNaN(area.max_daily_rate)) {
    return formatRateAmount(area.max_daily_rate, area.currency);
  }
  if (area.currency?.trim()) return area.currency.trim();
  return null;
}

const ASSIGNMENT_OPTIONS: { value: ParkingAreaDeviceAssignment; labelKey: string }[] = [
  { value: 'GENERAL', labelKey: 'parkingAreas.assignmentGeneral' },
  { value: 'ENTRY', labelKey: 'parkingAreas.assignmentEntry' },
  { value: 'EXIT', labelKey: 'parkingAreas.assignmentExit' },
  { value: 'ENTRY_EXIT', labelKey: 'parkingAreas.assignmentEntryExit' },
  { value: 'SENSOR', labelKey: 'parkingAreas.assignmentSensor' },
];

function flagsForAssignment(role: ParkingAreaDeviceAssignment): {
  association_type?: string;
  is_entry?: boolean;
  is_exit?: boolean;
} {
  switch (role) {
    case 'ENTRY':
      return { association_type: role, is_entry: true, is_exit: false };
    case 'EXIT':
      return { association_type: role, is_entry: false, is_exit: true };
    case 'ENTRY_EXIT':
      return { association_type: role, is_entry: true, is_exit: true };
    default:
      return { association_type: role, is_entry: false, is_exit: false };
  }
}

const ParkingAreasPage: React.FC = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const { user, token } = useAuthStore();
  const isSystem = user?.role === 'SYSTEM';

  const [areas, setAreas] = useState<ParkingArea[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [nameFilter, setNameFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const formModal = useDisclosure();
  const addDeviceModal = useDisclosure();
  const [areaForDevice, setAreaForDevice] = useState<ParkingArea | null>(null);
  const [editingArea, setEditingArea] = useState<ParkingArea | null>(null);

  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState<ParkingAreaType>(ParkingAreaType.AREA);
  const [formTotalSpots, setFormTotalSpots] = useState<number>(0);
  const [formIsPublic, setFormIsPublic] = useState(false);
  const [formCurrency, setFormCurrency] = useState('');
  const [formHourlyRate, setFormHourlyRate] = useState('');
  const [formFirstHourRate, setFormFirstHourRate] = useState('');
  const [formMaxDailyRate, setFormMaxDailyRate] = useState('');
  const [formFreeMinutes, setFormFreeMinutes] = useState('');
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const [devicesOptions, setDevicesOptions] = useState<Device[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [assignmentRole, setAssignmentRole] = useState<ParkingAreaDeviceAssignment>('GENERAL');
  const [isSubmittingAssign, setIsSubmittingAssign] = useState(false);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);

  const fetchAreas = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      apiClient.client.setToken(token);

      const filters: Record<string, string | number | undefined> = {};
      if (nameFilter.trim()) filters.name = nameFilter.trim();
      if (typeFilter) filters.type = typeFilter;
      if (sortBy) {
        filters.sort_by = sortBy;
        filters.sort_direction = sortDirection;
      }

      const listFn = isSystem
        ? apiClient.parkingAreas.listAllPaginated.bind(apiClient.parkingAreas)
        : apiClient.parkingAreas.listPaginated.bind(apiClient.parkingAreas);

      const listResp = await listFn({ page: currentPage, limit: pageSize }, filters);

      const detailed = await Promise.all(
        listResp.data.map(async (a) => {
          try {
            const one = await apiClient.parkingAreas.get(a.id);
            return one.data;
          } catch {
            return a;
          }
        })
      );

      setAreas(detailed);
      setTotalItems(listResp.pagination.total);
    } catch (error) {
      toast({
        title: t('parkingAreas.loadErrorTitle'),
        description: error instanceof Error ? error.message : t('common.unknown'),
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    token,
    isSystem,
    currentPage,
    pageSize,
    nameFilter,
    typeFilter,
    sortBy,
    sortDirection,
    toast,
    t,
  ]);

  useEffect(() => {
    fetchAreas();
  }, [fetchAreas]);

  const refreshSingleArea = useCallback(
    async (id: string) => {
      if (!token) return;
      apiClient.client.setToken(token);
      const one = await apiClient.parkingAreas.get(id);
      setAreas((prev) => prev.map((a) => (a.id === id ? one.data : a)));
    },
    [token]
  );

  const resetFormFields = () => {
    setFormName('');
    setFormType(ParkingAreaType.AREA);
    setFormTotalSpots(0);
    setFormIsPublic(false);
    setFormCurrency('');
    setFormHourlyRate('');
    setFormFirstHourRate('');
    setFormMaxDailyRate('');
    setFormFreeMinutes('');
  };

  const applyAreaToForm = (area: ParkingArea) => {
    setFormName(area.name ?? '');
    setFormType(area.type);
    setFormTotalSpots(area.total_spots ?? 0);
    setFormIsPublic(Boolean(area.is_public));
    setFormCurrency(area.currency?.trim() ?? '');
    setFormHourlyRate(rateInputValue(area.hourly_rate));
    setFormFirstHourRate(rateInputValue(area.first_hour_rate));
    setFormMaxDailyRate(rateInputValue(area.max_daily_rate));
    setFormFreeMinutes(rateInputValue(area.free_minutes));
  };

  const buildRentalFields = (): Pick<
    ParkingAreaCreateParams,
    'currency' | 'hourly_rate' | 'first_hour_rate' | 'max_daily_rate' | 'free_minutes'
  > => {
    const fields: Pick<
      ParkingAreaCreateParams,
      'currency' | 'hourly_rate' | 'first_hour_rate' | 'max_daily_rate' | 'free_minutes'
    > = {};
    const currency = formCurrency.trim();
    if (currency) fields.currency = currency;
    const hourlyRate = optionalNumberFromInput(formHourlyRate);
    if (hourlyRate != null) fields.hourly_rate = hourlyRate;
    const firstHourRate = optionalNumberFromInput(formFirstHourRate);
    if (firstHourRate != null) fields.first_hour_rate = firstHourRate;
    const maxDailyRate = optionalNumberFromInput(formMaxDailyRate);
    if (maxDailyRate != null) fields.max_daily_rate = maxDailyRate;
    const freeMinutes = optionalNumberFromInput(formFreeMinutes);
    if (freeMinutes != null) fields.free_minutes = freeMinutes;
    return fields;
  };

  const openCreate = () => {
    setEditingArea(null);
    resetFormFields();
    formModal.onOpen();
  };

  const openEdit = (area: ParkingArea) => {
    setEditingArea(area);
    applyAreaToForm(area);
    formModal.onOpen();
  };

  const closeFormModal = () => {
    formModal.onClose();
    setEditingArea(null);
  };

  const submitForm = async () => {
    if (!token) return;
    setIsSubmittingForm(true);
    try {
      apiClient.client.setToken(token);
      const rental = buildRentalFields();

      if (editingArea) {
        const payload: ParkingAreaUpdateParams = {
          name: formName.trim(),
          type: formType,
          is_public: formIsPublic,
          ...rental,
        };
        if (formType === ParkingAreaType.AREA) {
          payload.total_spots = formTotalSpots;
        }
        await apiClient.parkingAreas.update(editingArea.id, payload);
        toast({
          title: t('parkingAreas.updateSuccess'),
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        const updatedId = editingArea.id;
        closeFormModal();
        await refreshSingleArea(updatedId);
      } else {
        const payload: ParkingAreaCreateParams = {
          name: formName.trim(),
          type: formType,
          is_public: formIsPublic,
          ...rental,
        };
        if (formType === ParkingAreaType.AREA) {
          payload.total_spots = formTotalSpots;
        }
        await apiClient.parkingAreas.create(payload);
        toast({
          title: t('parkingAreas.createSuccess'),
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        closeFormModal();
        setCurrentPage(1);
        await fetchAreas();
      }
    } catch (error) {
      toast({
        title: editingArea ? t('parkingAreas.updateErrorTitle') : t('parkingAreas.createErrorTitle'),
        description: error instanceof Error ? error.message : t('common.unknown'),
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingForm(false);
    }
  };

  const openAddDevice = async (area: ParkingArea) => {
    setAreaForDevice(area);
    setSelectedDeviceId('');
    setAssignmentRole('GENERAL');
    addDeviceModal.onOpen();
    if (!token) return;
    setIsLoadingDevices(true);
    try {
      apiClient.client.setToken(token);
      const params: Record<string, string> = {};
      if (!isSystem && user?.tenantId) {
        params.tenant_id = user.tenantId;
      }
      const devRes = await apiClient.devices.listPaginated({ page: 1, limit: 2000 }, params);
      const linked = new Set((area.devices ?? []).map((d) => d.device_id));
      setDevicesOptions(devRes.data.filter((d) => !linked.has(d.id)));
    } catch (error) {
      toast({
        title: t('parkingAreas.devicesLoadError'),
        description: error instanceof Error ? error.message : t('common.unknown'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setDevicesOptions([]);
    } finally {
      setIsLoadingDevices(false);
    }
  };

  const submitAddDevice = async () => {
    if (!token || !areaForDevice || !selectedDeviceId) return;
    setIsSubmittingAssign(true);
    try {
      apiClient.client.setToken(token);
      const flags = flagsForAssignment(assignmentRole);
      await apiClient.devices.assignToParkingArea(selectedDeviceId, {
        parking_area_id: areaForDevice.id,
        ...flags,
      });
      toast({
        title: t('parkingAreas.assignSuccess'),
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      addDeviceModal.onClose();
      await refreshSingleArea(areaForDevice.id);
    } catch (error) {
      toast({
        title: t('parkingAreas.assignErrorTitle'),
        description: error instanceof Error ? error.message : t('common.unknown'),
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingAssign(false);
    }
  };

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalItems / pageSize)),
    [totalItems, pageSize]
  );

  const deviceLabel = (row: ParkingAreaDeviceRow) => {
    const d = row.device;
    if (d?.nick_name) return d.nick_name;
    if (d?.serial_no) return d.serial_no;
    return row.device_id;
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={3}>
        <Heading size="lg">{t('parkingAreas.title')}</Heading>
        <HStack flexWrap="wrap">
          <IconButton
            aria-label={t('common.refresh')}
            icon={<FiRefreshCw />}
            onClick={() => fetchAreas()}
            isLoading={isLoading}
            variant="outline"
          />
          <Button leftIcon={<FiPlus />} colorScheme="orange" onClick={openCreate}>
            {t('parkingAreas.createArea')}
          </Button>
        </HStack>
      </Flex>

      <Flex mb={4} gap={3} flexWrap="wrap" align="end">
        <FormControl maxW="240px">
          <FormLabel mb={1}>{t('common.search')}</FormLabel>
          <Input
            placeholder={t('parkingAreas.searchNamePlaceholder')}
            value={nameFilter}
            onChange={(e) => {
              setNameFilter(e.target.value);
              setCurrentPage(1);
            }}
          />
        </FormControl>
        <FormControl maxW="200px">
          <FormLabel mb={1}>{t('common.filter')}</FormLabel>
          <Select
            placeholder={t('parkingAreas.allTypes')}
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value={ParkingAreaType.AREA}>AREA</option>
            <option value={ParkingAreaType.GROUP}>GROUP</option>
            <option value={ParkingAreaType.SINGLE}>SINGLE</option>
          </Select>
        </FormControl>
        <FormControl maxW="200px">
          <FormLabel mb={1}>{t('parkingAreas.sortBy')}</FormLabel>
          <Select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="created_at">{t('parkingAreas.sortCreated')}</option>
            <option value="name">{t('common.name')}</option>
            <option value="total_spots">{t('parkingAreas.sortTotalSpots')}</option>
            <option value="available_spots">{t('parkingAreas.sortAvailable')}</option>
          </Select>
        </FormControl>
        <FormControl maxW="140px">
          <FormLabel mb={1}>{t('parkingAreas.order')}</FormLabel>
          <Select
            value={sortDirection}
            onChange={(e) => {
              setSortDirection(e.target.value as 'asc' | 'desc');
              setCurrentPage(1);
            }}
          >
            <option value="desc">{t('parkingAreas.desc')}</option>
            <option value="asc">{t('parkingAreas.asc')}</option>
          </Select>
        </FormControl>
      </Flex>

      {isLoading && areas.length === 0 ? (
        <Flex justify="center" py={12}>
          <Spinner size="lg" />
        </Flex>
      ) : areas.length === 0 ? (
        <Text color="gray.500">{t('common.noData')}</Text>
      ) : (
        <Accordion allowMultiple reduceMotion>
          {areas.map((area) => {
            const activation = resolveParkingAreaActivation(area);
            const rentInline = rentalSummaryInline(area);
            const activationBadge =
              activation === 'active' ? (
                <Badge colorScheme="green">{t('parkingAreas.statusActive')}</Badge>
              ) : activation === 'inactive' ? (
                <Badge colorScheme="red">{t('parkingAreas.statusInactive')}</Badge>
              ) : (
                <Badge colorScheme="gray">{t('parkingAreas.statusUnknown')}</Badge>
              );
            const rawStatus = (area as ParkingAreaExtended).status;
            const activationLabel =
              activation === 'active'
                ? t('parkingAreas.statusActive')
                : activation === 'inactive'
                  ? t('parkingAreas.statusInactive')
                  : rawStatus
                    ? t('parkingAreas.statusUnknownWithRaw', { raw: rawStatus })
                    : t('parkingAreas.statusUnknown');

            return (
            <AccordionItem key={area.id} border="1px solid" borderColor="gray.200" borderRadius="md" mb={2}>
              <h2>
                <AccordionButton py={3} _expanded={{ bg: 'gray.50' }}>
                  <Flex flex="1" textAlign="left" align="center" gap={3} flexWrap="wrap">
                    <Text fontWeight="semibold">{area.name}</Text>
                    {activationBadge}
                    <Badge>{area.type}</Badge>
                    {area.total_spots != null && (
                      <Text fontSize="sm" color="gray.600">
                        {t('parkingAreas.spots', { total: area.total_spots, avail: area.available_spots ?? '—' })}
                      </Text>
                    )}
                    {rentInline && (
                      <Text fontSize="sm" color="gray.600">
                        {rentInline}
                      </Text>
                    )}
                    <Text fontSize="sm" color="gray.600">
                      {t('parkingAreas.deviceCount', { count: area.devices?.length ?? 0 })}
                    </Text>
                  </Flex>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={4}>
                <Box
                  mb={4}
                  p={3}
                  bg="gray.50"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="gray.200"
                >
                  <Text fontWeight="semibold" fontSize="sm" mb={3}>
                    {t('parkingAreas.sectionStatusAndRates')}
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} fontSize="sm">
                    <Flex justify="space-between" gap={2} borderBottomWidth="1px" borderColor="gray.200" pb={2}>
                      <Text color="gray.600">{t('parkingAreas.colActivation')}</Text>
                      <Text fontWeight="medium">{activationLabel}</Text>
                    </Flex>
                    <Flex justify="space-between" gap={2} borderBottomWidth="1px" borderColor="gray.200" pb={2}>
                      <Text color="gray.600">{t('parkingAreas.colCurrency')}</Text>
                      <Text fontWeight="medium">{area.currency?.trim() || '—'}</Text>
                    </Flex>
                    <Flex justify="space-between" gap={2} borderBottomWidth="1px" borderColor="gray.200" pb={2}>
                      <Text color="gray.600">{t('parkingAreas.hourlyRate')}</Text>
                      <Text fontWeight="medium">{formatRateAmount(area.hourly_rate, area.currency)}</Text>
                    </Flex>
                    <Flex justify="space-between" gap={2} borderBottomWidth="1px" borderColor="gray.200" pb={2}>
                      <Text color="gray.600">{t('parkingAreas.firstHourRate')}</Text>
                      <Text fontWeight="medium">{formatRateAmount(area.first_hour_rate, area.currency)}</Text>
                    </Flex>
                    <Flex justify="space-between" gap={2} borderBottomWidth="1px" borderColor="gray.200" pb={2}>
                      <Text color="gray.600">{t('parkingAreas.maxDailyRate')}</Text>
                      <Text fontWeight="medium">{formatRateAmount(area.max_daily_rate, area.currency)}</Text>
                    </Flex>
                    <Flex justify="space-between" gap={2} pb={1}>
                      <Text color="gray.600">{t('parkingAreas.freeMinutes')}</Text>
                      <Text fontWeight="medium">
                        {area.free_minutes != null && !Number.isNaN(area.free_minutes)
                          ? t('parkingAreas.freeMinutesValue', { count: area.free_minutes })
                          : '—'}
                      </Text>
                    </Flex>
                  </SimpleGrid>
                </Box>
                <Flex justify="flex-end" mb={2} gap={2} flexWrap="wrap">
                  <Button
                    size="sm"
                    leftIcon={<FiEdit2 />}
                    variant="outline"
                    onClick={() => openEdit(area)}
                  >
                    {t('common.edit')}
                  </Button>
                  <Button size="sm" leftIcon={<FiPlus />} onClick={() => openAddDevice(area)}>
                    {t('parkingAreas.addDevice')}
                  </Button>
                </Flex>
                {(area.devices?.length ?? 0) === 0 ? (
                  <Text fontSize="sm" color="gray.500">
                    {t('parkingAreas.noDevices')}
                  </Text>
                ) : (
                  <TableContainer>
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>{t('parkingAreas.colDevice')}</Th>
                          <Th>{t('parkingAreas.colSerial')}</Th>
                          <Th>{t('parkingAreas.colAssignment')}</Th>
                          <Th>{t('common.status')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {(area.devices ?? []).map((row) => (
                          <Tr key={`${row.parking_area_id}-${row.device_id}`}>
                            <Td>{deviceLabel(row)}</Td>
                            <Td>{row.device?.serial_no ?? '—'}</Td>
                            <Td>{row.assignment ?? row.association_type ?? '—'}</Td>
                            <Td>{row.device?.status ?? '—'}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionPanel>
            </AccordionItem>
            );
          })}
        </Accordion>
      )}

      <Flex justify="space-between" align="center" mt={6} flexWrap="wrap" gap={3}>
        <Text fontSize="sm" color="gray.600">
          {t('parkingAreas.paginationSummary', {
            from: totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1,
            to: Math.min(currentPage * pageSize, totalItems),
            total: totalItems,
          })}
        </Text>
        <HStack>
          <Select
            size="sm"
            maxW="100px"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {[10, 25, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
          <Button
            size="sm"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            isDisabled={currentPage <= 1 || isLoading}
          >
            {t('common.previous')}
          </Button>
          <Text fontSize="sm">
            {currentPage} / {totalPages}
          </Text>
          <Button
            size="sm"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            isDisabled={currentPage >= totalPages || isLoading}
          >
            {t('common.next')}
          </Button>
        </HStack>
      </Flex>

      <Modal isOpen={formModal.isOpen} onClose={closeFormModal} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingArea ? t('parkingAreas.editModalTitle') : t('parkingAreas.createModalTitle')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4} isRequired>
              <FormLabel>{t('common.name')}</FormLabel>
              <Input value={formName} onChange={(e) => setFormName(e.target.value)} />
            </FormControl>
            <FormControl mb={4} isRequired>
              <FormLabel>{t('parkingAreas.fieldType')}</FormLabel>
              <Select value={formType} onChange={(e) => setFormType(e.target.value as ParkingAreaType)}>
                <option value={ParkingAreaType.AREA}>AREA</option>
                <option value={ParkingAreaType.GROUP}>GROUP</option>
                <option value={ParkingAreaType.SINGLE}>SINGLE</option>
              </Select>
            </FormControl>
            {formType === ParkingAreaType.AREA && (
              <FormControl mb={4} isRequired>
                <FormLabel>{t('parkingAreas.fieldTotalSpots')}</FormLabel>
                <Input
                  type="number"
                  min={0}
                  value={formTotalSpots}
                  onChange={(e) => setFormTotalSpots(Number(e.target.value))}
                />
              </FormControl>
            )}
            <FormControl display="flex" alignItems="center" mb={4}>
              <FormLabel mb="0">{t('parkingAreas.fieldPublic')}</FormLabel>
              <Switch isChecked={formIsPublic} onChange={(e) => setFormIsPublic(e.target.checked)} />
            </FormControl>

            <Box
              p={4}
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="md"
              bg="gray.50"
            >
              <Text fontWeight="semibold" fontSize="sm" mb={1}>
                {t('parkingAreas.sectionRentalRates')}
              </Text>
              <Text fontSize="xs" color="gray.500" mb={3}>
                {t('parkingAreas.rentalRatesHint')}
              </Text>
              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                <FormControl>
                  <FormLabel fontSize="sm">{t('parkingAreas.colCurrency')}</FormLabel>
                  <Select
                    placeholder={t('parkingAreas.currencyPlaceholder')}
                    value={formCurrency}
                    onChange={(e) => setFormCurrency(e.target.value)}
                    bg="white"
                  >
                    <option value="TRY">TRY</option>
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('parkingAreas.hourlyRate')}</FormLabel>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="0.00"
                    value={formHourlyRate}
                    onChange={(e) => setFormHourlyRate(e.target.value)}
                    bg="white"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('parkingAreas.firstHourRate')}</FormLabel>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="0.00"
                    value={formFirstHourRate}
                    onChange={(e) => setFormFirstHourRate(e.target.value)}
                    bg="white"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('parkingAreas.maxDailyRate')}</FormLabel>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="0.00"
                    value={formMaxDailyRate}
                    onChange={(e) => setFormMaxDailyRate(e.target.value)}
                    bg="white"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('parkingAreas.freeMinutes')}</FormLabel>
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    placeholder="0"
                    value={formFreeMinutes}
                    onChange={(e) => setFormFreeMinutes(e.target.value)}
                    bg="white"
                  />
                </FormControl>
              </SimpleGrid>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeFormModal}>
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme="orange"
              onClick={submitForm}
              isLoading={isSubmittingForm}
              isDisabled={!formName.trim() || (formType === ParkingAreaType.AREA && formTotalSpots < 0)}
            >
              {editingArea ? t('common.save') : t('common.create')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={addDeviceModal.isOpen} onClose={addDeviceModal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t('parkingAreas.addDeviceModalTitle', { name: areaForDevice?.name ?? '' })}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isLoadingDevices ? (
              <Flex justify="center" py={8}>
                <Spinner />
              </Flex>
            ) : (
              <>
                <FormControl mb={4} isRequired>
                  <FormLabel>{t('navigation.devices')}</FormLabel>
                  <Select
                    placeholder={t('parkingAreas.selectDevice')}
                    value={selectedDeviceId}
                    onChange={(e) => setSelectedDeviceId(e.target.value)}
                  >
                    {devicesOptions.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.nick_name} ({d.serial_no})
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>{t('parkingAreas.assignmentLabel')}</FormLabel>
                  <Select
                    value={assignmentRole}
                    onChange={(e) => setAssignmentRole(e.target.value as ParkingAreaDeviceAssignment)}
                  >
                    {ASSIGNMENT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {t(opt.labelKey)}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={addDeviceModal.onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme="orange"
              onClick={submitAddDevice}
              isLoading={isSubmittingAssign}
              isDisabled={!selectedDeviceId || isLoadingDevices}
            >
              {t('parkingAreas.addDevice')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ParkingAreasPage;
